import React, { useEffect } from 'react';

import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { accountsByIdStatusSelector, accountsSelector } from '../account/account-selectors';
import { loadAccounts } from '../account/actions/load-accounts';
import {
  accountGroupsForUsernameSelector,
  accountGroupsStateForUsernameSelector,
  accountGroupsForIdSelector,
} from '../account-group/account-group-selectors';
import { loadAccountGroups } from '../account-group/actions/load-account-groups';
import { accountScoreForAccountIdSelector } from '../account-score-generation-log/account-score-generation-log-selectors';
import { loadRecentAccountScoreGenerationLog } from '../account-score-generation-log/actions/load-recent-account-score-generation-log';
import { userSelector } from '../reducers/current-user-selectors';
import type { RequestState } from '../reducers/utils/state-manager';

import { AccountsListAdminControls } from './accounts-list-admin-controls';
import { ACCOUNT_LIST_BASIC_COLUMNS, ACCOUNT_LIST_ADMIN_COLUMNS } from './accounts-list-columns';
import { AccountsListTableWithControls } from './accounts-list-table-with-controls';

import { hasPermissionIfTHE } from '../config/special-customer-permission';
import { LoadingMessage } from '../widgets/loading-message';

const mapStateToProps = () => (state: any) => {
  const user = userSelector(state);
  let accounts: TeamEngineAccount[] = [];
  let accountsState: Partial<RequestState> = {};
  let accountGroup: AccountGroup | undefined;

  if (user.isAdmin) {
    accounts = accountsSelector(state);
    accountsState = accountsByIdStatusSelector(state);
  } else if (user.accountGroupId) {
    accounts = accountGroupsForUsernameSelector(state, user.username).flatMap((ag) => ag.Accounts);
    accountsState = accountGroupsStateForUsernameSelector(state, user.username);
    accountGroup = accountGroupsForIdSelector(state, user.accountGroupId);
  }

  return {
    user,
    accounts,
    accountsState,
    accountScoreSelector: (accountId: number): AccountScore | undefined =>
      user.isAdmin ? accountScoreForAccountIdSelector(state, accountId) : undefined,
    accountGroup,
  };
};

export interface AccountsWithScore extends TeamEngineAccount {
  totalScore?: number | undefined;
}

export const AccountsListPage = () => {
  const { user, accounts, accountsState, accountScoreSelector, accountGroup } = useSelector(
    mapStateToProps(),
    shallowEqual,
  );

  const dispatch = useDispatch();
  useEffect(() => {
    if (user.isAdmin) {
      dispatch(loadAccounts());
      dispatch(loadRecentAccountScoreGenerationLog());
    } else if (user.username) {
      dispatch(loadAccountGroups(user.username));
    }
  });

  if (!accounts && accountsState.loading) {
    return <LoadingMessage />;
  }

  let modifiedAccounts = accounts;
  if (user.isAdmin) {
    const accountsWithScore: AccountsWithScore[] = accounts.map((account) => ({
      ...account,
      totalScore: accountScoreSelector(account.accountId)?.totalScore,
    }));
    modifiedAccounts = accountsWithScore.filter((account) =>
      account.Organizations.map((org) => hasPermissionIfTHE(org.org_id, user)).every(Boolean),
    );
  }

  return (
    <div className="u-padding-xl u-width-100 u-margin-bottom-clearChat">
      {user.isAdmin && <AccountsListAdminControls />}
      {!!accountGroup && <div className="typo-h5">{accountGroup.name}</div>}
      <AccountsListTableWithControls
        accounts={modifiedAccounts}
        columns={user.isAdmin ? ACCOUNT_LIST_ADMIN_COLUMNS : ACCOUNT_LIST_BASIC_COLUMNS}
        defaultSortBy={user.isAdmin ? 'firstHistoryEntry' : 'accountName'}
        showAccountActions={user.isAdmin}
      />
    </div>
  );
};
